import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { preProjectService, clientService } from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from './ConfirmModal';

const PreProjects = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const dropdownRef = useRef(null);
  const cepInputRef = useRef(null);

  const [confirmBudget, setConfirmBudget] = useState({ show: false, projectId: null });
  const [confirmConvert, setConfirmConvert] = useState({ show: false, projectId: null });

  // Verificar se é gerente
  const isGerente = user?.role === 'GERENCIA';

  // Query para buscar clientes com debounce
  const { data: clientsData, isLoading: isLoadingClients } = useQuery({
    queryKey: ['clients', clientSearch],
    queryFn: () => clientService.getClients({ search: clientSearch, limit: 10 }),
    enabled: clientSearch.length >= 2,
  });

  const clients = clientsData?.data?.data || [];

  // Estados para o formulário de novo cliente
  const [clientFormData, setClientFormData] = useState({
    full_name: '',
    apelido: '',
    email: '',
    phone: '',
    document_id: '',
    birth_date: '',
    address: '',
  });
  const [addressFields, setAddressFields] = useState({
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });

  // Mutation para criar cliente
  const createClientMutation = useMutation({
    mutationFn: (data) => clientService.createClient(data),
    onSuccess: (response) => {
      // Verificar se response é um erro do axios ou resposta real
      if (response?.response?.status >= 400) {
        // É um erro, não fazer nada (o onError cuida)
        return;
      }
      toast.success('Cliente cadastrado com sucesso!');
      const newClient = response.data?.data || response;
      // Preencher campos do cliente selecionado
      setSelectedClient(newClient);
      setValue('client_name', newClient.full_name || newClient.name || '');
      setValue('client_phone', newClient.phone || '');
      setValue('client_email', newClient.email || '');
      setValue('client_address', newClient.address || '');
      setClientSearch(newClient.full_name || newClient.name || '');
      setShowClientDropdown(false);
      setShowClientModal(false);
      // Limpar formulário
      setClientFormData({
        full_name: '',
        apelido: '',
        email: '',
        phone: '',
        document_id: '',
        birth_date: '',
        address: '',
      });
      setAddressFields({
        cep: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: ''
      });
    },
    onError: (error) => {
      const message = error.response?.data?.message || error.response?.data?.error || 'Erro ao cadastrar cliente';
      toast.error(message);
    }
  });

  // Effect para fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Função para lidar com mudança na busca de cliente
  const handleClientSearchChange = (e) => {
    const value = e.target.value;
    setClientSearch(value);
    setSelectedClient(null);
    setShowClientDropdown(value.length >= 2);
  };

  // Função para selecionar cliente
  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setClientSearch(client.full_name || client.name || '');
    setValue('client_name', client.full_name || client.name || '');
    setValue('client_phone', client.phone || '');
    setValue('client_email', client.email || '');
    setValue('client_address', client.address || '');
    setShowClientDropdown(false);
  };

  // Função para abrir modal de novo cliente
  const handleOpenNewClient = () => {
    setShowClientDropdown(false);
    setShowClientModal(true);
  };

  // Função para construir endereço completo
  const buildFullAddress = () => {
    const parts = [];
    if (addressFields.street) parts.push(addressFields.street);
    if (addressFields.number) parts.push(`nº ${addressFields.number}`);
    if (addressFields.complement) parts.push(addressFields.complement);
    if (addressFields.neighborhood) parts.push(addressFields.neighborhood);
    if (addressFields.city) parts.push(addressFields.city);
    if (addressFields.state) parts.push(addressFields.state);
    if (addressFields.cep) parts.push(`CEP: ${addressFields.cep}`);
    return parts.join(', ');
  };

  // Função para aplicar máscara de telefone
  const maskPhone = (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    } else {
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2')
        .replace(/(-\d{4})\d+?$/, '$1');
    }
  };

  // Função para aplicar máscara de CPF
  const maskCPF = (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  // Função para validar CPF
  const isValidCPF = (cpf) => {
    if (!cpf) return false;
    const numbers = cpf.replace(/\D/g, '');
    if (numbers.length !== 11) return false;
    return true;
  };

  // Função para submeter formulário de novo cliente
  const handleClientSubmit = (e) => {
    e.preventDefault();

    // Validar campos obrigatórios
    if (!clientFormData.full_name || clientFormData.full_name.trim().length < 3) {
      toast.error('Nome é obrigatório (mínimo 3 caracteres)');
      return;
    }
    if (!clientFormData.apelido || clientFormData.apelido.trim().length === 0) {
      toast.error('Apelido é obrigatório');
      return;
    }
    if (!clientFormData.phone || clientFormData.phone.trim().length === 0) {
      toast.error('Telefone é obrigatório');
      return;
    }
    // Validar CPF ou CNPJ (opcional - se fornecido, deve ter 11 ou 14 dígitos)
    const docClean = (clientFormData.document_id || '').replace(/\D/g, '');
    if (clientFormData.document_id && docClean.length !== 11 && docClean.length !== 14) {
      toast.error('CPF (11 dígitos) ou CNPJ (14 dígitos) inválido');
      return;
    }

    const fullAddress = buildFullAddress();

    // Converter strings vazias para null em campos opcionais
    const submitData = {
      full_name: clientFormData.full_name,
      apelido: clientFormData.apelido,
      email: clientFormData.email || null,
      phone: clientFormData.phone || null,
      document_id: clientFormData.document_id || null,
      birth_date: clientFormData.birth_date || null,
      address: fullAddress || clientFormData.address || null
    };

    createClientMutation.mutate(submitData);
  };

  // Função para buscar CEP
  const fetchCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/utils/cep/${cleanCep}`);
      const data = await response.json();
      if (!data.erro) {
        setAddressFields(prev => ({
          ...prev,
          street: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || '',
          complement: data.complemento || ''
        }));
        toast.success('CEP encontrado!');
      } else {
        toast.error('CEP não encontrado');
      }
    } catch (error) {
      toast.error('Erro ao buscar CEP');
    }
  };

  // Query para buscar pré-projetos
  const { data: projectsData, isLoading, refetch } = useQuery({
    queryKey: ['pre-projects', filterStatus],
    queryFn: () => preProjectService.getAll({
      status: filterStatus || undefined,
      limit: 100
    })
  });

  // Query para buscar statuses
  const { data: statusesData } = useQuery({
    queryKey: ['pre-project-statuses'],
    queryFn: () => preProjectService.getStatuses()
  });

  const projects = projectsData?.data?.data || [];
  const statuses = statusesData?.data?.data || [];

  const { register, handleSubmit, reset, formState: { errors, isSubmitting }, setValue } = useForm({
    defaultValues: {
      client_name: '',
      client_phone: '',
      client_email: '',
      client_address: '',
      description: '',
      complexity_level: '',
      budget_range: '',
      desired_deadline: '',
      contact_preference: '',
      material_preferences: '',
      observations: ''
    }
  });

  // Mutation para criar pré-projeto
  const createMutation = useMutation({
    mutationFn: (data) => preProjectService.create(data),
    onSuccess: (response) => {
      toast.success('Pré-projeto criado com sucesso!');
      reset();
      setSelectedClient(null);
      setClientSearch('');
      setAttachments([]);
      setShowCreateForm(false);
      queryClient.invalidateQueries(['pre-projects']);
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Erro ao criar pré-projeto';
      toast.error(message);
    }
  });

  // Mutation para atualizar status
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, notes }) => preProjectService.updateStatus(id, { status, notes }),
    onSuccess: () => {
      toast.success('Status atualizado com sucesso!');
      queryClient.invalidateQueries(['pre-projects']);
    },
    onError: (error) => {
      toast.error('Erro ao atualizar status');
    }
  });

  // Mutation para revisar (GERENCIA)
  const reviewMutation = useMutation({
    mutationFn: ({ id, data }) => preProjectService.review(id, data),
    onSuccess: () => {
      toast.success('Pré-projeto revisado com sucesso!');
      queryClient.invalidateQueries(['pre-projects']);
      setSelectedProject(null);
    },
    onError: (error) => {
      toast.error('Erro ao revisar pré-projeto');
    }
  });

  // Mutation para converter pré-projeto em projeto
  const convertToProjectMutation = useMutation({
    mutationFn: (id) => preProjectService.convertToProject(id),
    onSuccess: (response) => {
      toast.success('Pré-projeto convertido em projeto com sucesso!');
      queryClient.invalidateQueries(['pre-projects']);
      setSelectedProject(null);
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Erro ao converter em projeto';
      toast.error(message);
    }
  });

  const onSubmit = (data) => {
    // Adicionar anexos aos dados do formulário
    const submitData = {
      ...data,
      attachments: attachments
    };
    createMutation.mutate(submitData);
  };

  // Função para lidar com upload de arquivos (converte para base64)
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = [];

    for (const file of files) {
      try {
        const base64 = await convertToBase64(file);
        newAttachments.push({
          name: file.name,
          size: file.size,
          type: file.type,
          data: base64,
          uploadDate: new Date().toISOString()
        });
      } catch (error) {
        console.error('Erro ao converter arquivo:', error);
        toast.error(`Erro ao processar arquivo: ${file.name}`);
      }
    }

    setAttachments([...attachments, ...newAttachments]);
  };

  // Função para converter arquivo para base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Função para baixar arquivo
  const handleDownloadFile = (file) => {
    if (!file.data) {
      toast.error('Arquivo não disponível para download');
      return;
    }
    const link = document.createElement('a');
    link.href = file.data;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Função para remover arquivo
  const handleRemoveAttachment = (index) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);
  };

  // Função para formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleApprove = (project) => {
    if (!isGerente) {
      toast.error('Apenas gerência pode aprovar');
      return;
    }
    updateStatusMutation.mutate({
      id: project.id,
      status: 'ORCAMENTO_PRONTO',
      notes: 'Aprovado pela gerência'
    });
  };

  const handleReject = (project) => {
    if (!isGerente) {
      toast.error('Apenas gerência pode reprovar');
      return;
    }
    updateStatusMutation.mutate({
      id: project.id,
      status: 'CANCELADO',
      notes: 'Reprovado pela gerência'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      PENDENTE_ANALISE: 'bg-yellow-100 text-yellow-800',
      PRE_PROJETO: 'bg-blue-100 text-blue-800',
      ORCAMENTO_ENVIADO: 'bg-purple-100 text-purple-800',
      APROVADO_CLIENTE: 'bg-green-100 text-green-800',
      REPROVADO_CLIENTE: 'bg-red-100 text-red-800',
      CANCELADO: 'bg-gray-100 text-gray-800',
      CONVERTIDO: 'bg-indigo-100 text-indigo-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const statusItem = statuses.find(s => s.value === status);
    return statusItem?.label || status;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pré-Projetos</h1>
          <p className="text-gray-600">
            Gerencie pré-projetos antes da criação oficial do projeto
          </p>
        </div>
        <button
          onClick={() => {
            if (showCreateForm) {
              reset();
              setSelectedClient(null);
              setClientSearch('');
              setAttachments([]);
            }
            setShowCreateForm(!showCreateForm);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <span>{showCreateForm ? '✕ Fechar' : '+ Novo Pré-Projeto'}</span>
        </button>
      </div>

      {/* Formulário de criação */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Novo Pré-Projeto</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Dados do cliente */}
            <div className="md:col-span-2">
              <h3 className="font-medium text-gray-700 mb-2">Dados do Cliente</h3>
            </div>

            {/* Campo de busca de cliente com autocomplete */}
            <div className="md:col-span-2" ref={dropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar Cliente *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={clientSearch}
                  onChange={handleClientSearchChange}
                  onFocus={() => clientSearch.length >= 2 && setShowClientDropdown(true)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Digite o nome do cliente para buscar..."
                />
                <button
                  type="button"
                  onClick={handleOpenNewClient}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-[#49943d] text-white text-sm rounded hover:bg-[#3a7630] transition-colors"
                >
                  + Novo Cliente
                </button>

                {/* Dropdown de resultados */}
                {showClientDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {isLoadingClients ? (
                      <div className="px-3 py-2 text-gray-500">Buscando...</div>
                    ) : clients.length > 0 ? (
                      clients.map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleSelectClient(client)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{client.full_name || client.name}</div>
                          <div className="text-sm text-gray-500">
                            {client.phone} {client.email && `| ${client.email}`}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-500">
                        Nenhum cliente encontrado
                      </div>
                    )}
                  </div>
                )}
              </div>
              {selectedClient && (
                <p className="text-green-600 text-sm mt-1">Cliente selecionado: {selectedClient.full_name || selectedClient.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Cliente *
              </label>
              <input
                {...register('client_name', { required: 'Nome é obrigatório' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Nome completo"
              />
              {errors.client_name && (
                <p className="text-red-500 text-sm mt-1">{errors.client_name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <input
                {...register('client_phone')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="(00) 00000-0000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-mail
              </label>
              <input
                {...register('client_email')}
                type="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="email@exemplo.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Endereço
              </label>
              <input
                {...register('client_address')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Endereço completo"
              />
            </div>

            {/* Dados do projeto */}
            <div className="md:col-span-2">
              <h3 className="font-medium text-gray-700 mb-2 mt-4">Dados do Projeto</h3>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição / Necessidades
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Descreva as necessidades do cliente..."
              />
            </div>

            {/* Faixa de Orçamento - apenas para gerente */}
            {isGerente && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Faixa de Orçamento
                </label>
                <select
                  {...register('budget_range')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione...</option>
                  <option value="ATE_5K">Até R$ 5.000</option>
                  <option value="5K_10K">R$ 5.000 - R$ 10.000</option>
                  <option value="10K_20K">R$ 10.000 - R$ 20.000</option>
                  <option value="20K_50K">R$ 20.000 - R$ 50.000</option>
                  <option value="50K_100K">R$ 50.000 - R$ 100.000</option>
                  <option value="ACIMA_100K">Acima de R$ 100.000</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Prevista para Início
              </label>
              <input
                type="date"
                {...register('desired_deadline')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Complexidade Prevista - apenas para gerente */}
            {isGerente && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Complexidade Prevista
                </label>
                <select
                  {...register('complexity_level')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione...</option>
                  <option value="BAIXA">Baixa</option>
                  <option value="MEDIA">Média</option>
                  <option value="ALTA">Alta</option>
                  <option value="ESPECIAL">Especial</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferência de Contato
              </label>
              <select
                {...register('contact_preference')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione...</option>
                <option value="WHATSAPP">WhatsApp</option>
                <option value="TELEFONE">Telefone</option>
                <option value="EMAIL">E-mail</option>
                <option value="PRESENCIAL">Presencial</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferências de Material
              </label>
              <textarea
                {...register('material_preferences')}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Descreva as preferências de material..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observações
              </label>
              <textarea
                {...register('observations')}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Observações adicionais..."
              />
            </div>

            {/* Campo de Anexos */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Anexos
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                  "
                />
                {attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600 text-sm">
                            {file.type?.startsWith('image/') ? '🖼️' : '📎'}
                          </span>
                          <span className="text-sm text-gray-700">{file.name}</span>
                          <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {file.data && (
                            <button
                              type="button"
                              onClick={() => handleDownloadFile(file)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              ⬇️
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(index)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="md:col-span-2 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  reset();
                  setSelectedClient(null);
                  setClientSearch('');
                  setAttachments([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Limpar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Salvando...' : 'Criar Pré-Projeto'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Filtrar por status:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos</option>
            {statuses.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => refetch()}
            className="px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
          >
            Atualizar
          </button>
        </div>
      </div>

      {/* Lista de pré-projetos */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orçamento</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Criado em</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    Carregando...
                  </td>
                </tr>
              ) : projects.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    Nenhum pré-projeto encontrado
                  </td>
                </tr>
              ) : (
                projects.map(project => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{project.client_name}</div>
                      <div className="text-sm text-gray-500">{project.client_phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(project.status)}`}>
                        {getStatusLabel(project.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {project.budget_range || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(project.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedProject(project)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Ver detalhes
                        </button>
                        {isGerente && project.status === 'PENDENTE_ANALISE' && (
                          <>
                            <button
                              onClick={() => handleApprove(project)}
                              className="text-green-600 hover:text-green-800 text-sm"
                            >
                              Aprovar
                            </button>
                            <button
                              onClick={() => handleReject(project)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Reprovar
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de detalhes */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold">Detalhes do Pré-Projeto</h2>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-700">Cliente</h3>
                  <p className="text-gray-900">{selectedProject.client_name}</p>
                  <p className="text-gray-600">{selectedProject.client_phone}</p>
                  <p className="text-gray-600">{selectedProject.client_email}</p>
                  <p className="text-gray-600">{selectedProject.client_address}</p>
                </div>

                <div>
                  <h3 className="font-medium text-gray-700">Descrição</h3>
                  <p className="text-gray-900">{selectedProject.description || '-'}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-medium text-gray-700">Status</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(selectedProject.status)}`}>
                      {getStatusLabel(selectedProject.status)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700">Complexidade</h3>
                    <p className="text-gray-900">{selectedProject.complexity_level || '-'}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700">Faixa de Orçamento</h3>
                    <p className="text-gray-900">{selectedProject.budget_range || '-'}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700">Data Prevista para Início</h3>
                    <p className="text-gray-900">
                      {selectedProject.desired_deadline
                        ? new Date(selectedProject.desired_deadline).toLocaleDateString('pt-BR')
                        : '-'}
                    </p>
                  </div>
                </div>

                {selectedProject.observations && (
                  <div>
                    <h3 className="font-medium text-gray-700">Observações</h3>
                    <p className="text-gray-900">{selectedProject.observations}</p>
                  </div>
                )}

                {/* Exibir anexos */}
                {selectedProject.attachments && selectedProject.attachments.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-700">Anexos</h3>
                    <div className="space-y-1">
                      {selectedProject.attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span>{file.type?.startsWith('image/') ? '🖼️' : '📎'}</span>
                            <span className="text-sm text-gray-700">{file.name}</span>
                            <span className="text-xs text-gray-400">({formatFileSize(file.size)})</span>
                          </div>
                          {file.data && (
                            <button
                              onClick={() => handleDownloadFile(file)}
                              className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                            >
                              ⬇️ Baixar
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="text-sm text-gray-500">
                  Criado em: {new Date(selectedProject.created_at).toLocaleString('pt-BR')}
                  {selectedProject.reviewed_at && (
                    <> | Revisado em: {new Date(selectedProject.reviewed_at).toLocaleString('pt-BR')}</>
                  )}
                </div>
              </div>

              {/* Ações para gerência */}
              {isGerente && selectedProject.status === 'PENDENTE_ANALISE' && (
                <div className="mt-6 flex gap-2 justify-end border-t pt-4">
                  <button
                    onClick={() => {
                      updateStatusMutation.mutate({
                        id: selectedProject.id,
                        status: 'CANCELADO',
                        notes: 'Cancelado pelo gerente'
                      });
                      setSelectedProject(null);
                    }}
                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      reviewMutation.mutate({
                        id: selectedProject.id,
                        data: {
                          status: 'ORCAMENTO_PRONTO',
                          complexity_level: selectedProject.complexity_level || 'MEDIA',
                          observations: selectedProject.observations
                        }
                      });
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Aprovar como Pré-Projeto
                  </button>
                </div>
              )}

              {/* Ação para montar orçamento quando está em PRE_PROJETO */}
              {selectedProject.status === 'ORCAMENTO_PRONTO' && (
                <div className="mt-6 flex gap-2 justify-end border-t pt-4">
                  {selectedProject.survey_id ? (
                    <button
                      onClick={() => {
                        // Se já tem survey vinculado, navegar para orçamento
                        window.location.href = `/projetos/${selectedProject.survey_id}/orcamento`;
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      Montar Orçamento
                    </button>
                  ) : (
                    <div className="text-sm text-gray-500 bg-yellow-50 p-2 rounded">
                      ⚠️ Este pré-projeto precisa estar vinculado a um levantamento para criar orçamento.
                      <br />
                      Entre em contato com a gerência.
                    </div>
                  )}
                </div>
              )}

              {/* Ação para confirmar aprovação/reprovação do orçamento quando está em ORCAMENTO_ENVIADO */}
              {selectedProject.status === 'ORCAMENTO_ENVIADO' && (
                <div className="mt-6 flex gap-2 justify-end border-t pt-4">
                  <button
                    onClick={() => {
                      setConfirmBudget({ show: true, projectId: selectedProject.id });
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Confirmar Resposta do Cliente
                  </button>
                </div>
              )}

              {/* Ação para converter em projeto quando aprovado pelo cliente */}
              {isGerente && selectedProject.status === 'APROVADO_CLIENTE' && (
                <div className="mt-6 flex gap-2 justify-end border-t pt-4">
                  <button
                    onClick={() => {
                      setConfirmConvert({ show: true, projectId: selectedProject.id });
                    }}
                    disabled={convertToProjectMutation.isLoading}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {convertToProjectMutation.isLoading ? 'Convertendo...' : 'Converter em Projeto'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ConfirmModal for budget approval */}
      <ConfirmModal
        show={confirmBudget.show}
        onConfirm={() => {
          updateStatusMutation.mutate({
            id: confirmBudget.projectId,
            status: 'APROVADO_CLIENTE',
            notes: 'Orçamento aprovado pelo cliente'
          });
          setSelectedProject(null);
          toast.success('Orçamento aprovado! O pré-projeto será convertido em projeto.');
          setConfirmBudget({ show: false, projectId: null });
        }}
        onCancel={() => {
          updateStatusMutation.mutate({
            id: confirmBudget.projectId,
            status: 'REPROVADO_CLIENTE',
            notes: 'Orçamento reprovado pelo cliente'
          });
          setSelectedProject(null);
          toast.error('Orçamento reprovado pelo cliente.');
          setConfirmBudget({ show: false, projectId: null });
        }}
        title="Confirmar Resposta do Cliente"
        message="O cliente aprovou o orçamento? Clique em Confirmar para aprovar ou Cancelar para reprovar."
        confirmText="Aprovar"
        cancelText="Reprovar"
        type="success"
      />

      {/* ConfirmModal for conversion to project */}
      <ConfirmModal
        show={confirmConvert.show}
        onConfirm={() => {
          convertToProjectMutation.mutate(confirmConvert.projectId);
          setSelectedProject(null);
          setConfirmConvert({ show: false, projectId: null });
        }}
        onCancel={() => {
          setConfirmConvert({ show: false, projectId: null });
        }}
        title="Converter em Projeto"
        message="Tem certeza que deseja converter este pré-projeto em projeto? Esta ação não pode ser desfeita."
        confirmText="Converter"
        cancelText="Cancelar"
        type="warning"
      />

      {/* Modal de novo cliente */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-800">Novo Cliente</h2>
                <button
                  onClick={() => setShowClientModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleClientSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome completo *
                    </label>
                    <input
                      type="text"
                      value={clientFormData.full_name}
                      onChange={(e) => setClientFormData({ ...clientFormData, full_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Nome completo"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apelido *
                    </label>
                    <input
                      type="text"
                      value={clientFormData.apelido}
                      onChange={(e) => setClientFormData({ ...clientFormData, apelido: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Como o cliente será chamado"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={clientFormData.email}
                      onChange={(e) => setClientFormData({ ...clientFormData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="email@exemplo.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone *
                    </label>
                    <input
                      type="text"
                      value={clientFormData.phone}
                      onChange={(e) => setClientFormData({ ...clientFormData, phone: maskPhone(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="(00) 00000-0000"
                      required
                      maxLength={15}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CPF/CNPJ
                    </label>
                    <input
                      type="text"
                      value={clientFormData.document_id}
                      onChange={(e) => setClientFormData({ ...clientFormData, document_id: maskCPF(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Nascimento
                    </label>
                    <input
                      type="date"
                      value={clientFormData.birth_date}
                      onChange={(e) => setClientFormData({ ...clientFormData, birth_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* CEP */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CEP
                    </label>
                    <input
                      ref={cepInputRef}
                      type="text"
                      value={addressFields.cep}
                      onChange={(e) => setAddressFields(prev => ({ ...prev, cep: e.target.value }))}
                      onBlur={() => {
                        const value = cepInputRef.current?.value || addressFields.cep;
                        const cleanCep = value.replace(/\D/g, '');
                        if (cleanCep.length === 8) {
                          fetchCep(value);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="00000-000"
                      maxLength={9}
                    />
                  </div>

                  {/* Rua */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rua/Logradouro
                    </label>
                    <input
                      type="text"
                      value={addressFields.street}
                      onChange={(e) => setAddressFields({ ...addressFields, street: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Nome da rua"
                    />
                  </div>

                  {/* Número */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número
                    </label>
                    <input
                      type="text"
                      value={addressFields.number}
                      onChange={(e) => setAddressFields({ ...addressFields, number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="123"
                    />
                  </div>

                  {/* Complemento */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Complemento
                    </label>
                    <input
                      type="text"
                      value={addressFields.complement}
                      onChange={(e) => setAddressFields({ ...addressFields, complement: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Apto, Bloco, etc."
                    />
                  </div>

                  {/* Bairro */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bairro
                    </label>
                    <input
                      type="text"
                      value={addressFields.neighborhood}
                      onChange={(e) => setAddressFields({ ...addressFields, neighborhood: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Nome do bairro"
                    />
                  </div>

                  {/* Cidade */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cidade
                    </label>
                    <input
                      type="text"
                      value={addressFields.city}
                      onChange={(e) => setAddressFields({ ...addressFields, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Nome da cidade"
                    />
                  </div>

                  {/* Estado */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estado (UF)
                    </label>
                    <select
                      value={addressFields.state}
                      onChange={(e) => setAddressFields({ ...addressFields, state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione</option>
                      <option value="AC">Acre</option>
                      <option value="AL">Alagoas</option>
                      <option value="AP">Amapá</option>
                      <option value="AM">Amazonas</option>
                      <option value="BA">Bahia</option>
                      <option value="CE">Ceará</option>
                      <option value="DF">Distrito Federal</option>
                      <option value="ES">Espírito Santo</option>
                      <option value="GO">Goiás</option>
                      <option value="MA">Maranhão</option>
                      <option value="MT">Mato Grosso</option>
                      <option value="MS">Mato Grosso do Sul</option>
                      <option value="MG">Minas Gerais</option>
                      <option value="PA">Pará</option>
                      <option value="PB">Paraíba</option>
                      <option value="PR">Paraná</option>
                      <option value="PE">Pernambuco</option>
                      <option value="PI">Piauí</option>
                      <option value="RJ">Rio de Janeiro</option>
                      <option value="RN">Rio Grande do Norte</option>
                      <option value="RS">Rio Grande do Sul</option>
                      <option value="RO">Rondônia</option>
                      <option value="RR">Roraima</option>
                      <option value="SC">Santa Catarina</option>
                      <option value="SP">São Paulo</option>
                      <option value="SE">Sergipe</option>
                      <option value="TO">Tocantins</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowClientModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createClientMutation.isLoading}
                    className="px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] disabled:opacity-50"
                  >
                    {createClientMutation.isLoading ? 'Salvando...' : 'Cadastrar Cliente'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreProjects;
